import classNames from 'classnames/bind';
import { useId } from 'react';

import Chip from '../Chip/Chip';
import Dropdown, { DropdownProps } from '../Dropdown/Dropdown';
import Tooltip from '../Tooltip/Tooltip';
import Styles from './MultiSelectDropdown.module.scss';
import PortalTagMultiSelectDropdown from './PortalTagMultiSelectDropdown';

const cx = classNames.bind(Styles);

export interface Option<T> {
  value: T;
  label: string;
  tooltip?: string;
  isDisabled?: boolean;
}

export interface MultiSelectDropdownProps<T extends string | number | null> extends DropdownProps<Option<T>> {
  /** id will set the id for the dropdown and the label */
  id?: string;
  /** Array of objects containing Dropdown selections. */
  options: Option<T>[];
  /** Dropdown label text. if not set the label will be hidden */
  label?: string;
  /** Dropdown placeholder text. */
  placeholder?: string;
  /** Enables full width dropdown */
  fullWidth?: boolean;
  /** Dictates whether or not dropdown is disabled. */
  disabled?: boolean;
  /** on change will return the complete list of selected items add or remove */
  onChange?: (value: any) => void;
  /** Dropdown onChange callback to be called after updating Chips */
  selectOption?: (value: Option<T>, ...args) => void;
  /** Callback to handle removing a Chip from the list of selected options */
  removeOption?: (value: Option<T>, ...args) => void;
  /** Allow the user to select multiple of the same item, default: false */
  allowMultiSelect?: boolean;
  /** Allows the user to add new elements to the list: value and label will match */
  allowAddNew?: boolean;
  /** function to validate new items, if not provided all items will be allowed */
  validateNewItem?: (value: any) => boolean;
  /** function to transform new items, if provided, this function will run before validateNewItem */
  transformNewItem?: (value: any) => void;
  /** newOption callback to be called when a new item is added */
  newOption?: (value: any) => void;
  /** The currently selected values */
  values: Option<T>[];
}

const MultiSelectDropdown = <T extends string | number | null>(props: MultiSelectDropdownProps<T>) => {
  const {
    allowAddNew,
    allowMultiSelect,
    disabled,
    id,
    label,
    newOption,
    onChange,
    options = [],
    placeholder,
    fullWidth,
    removeOption,
    selectOption,
    validateNewItem,
    transformNewItem,
    values,
    ...otherProps
  } = props;

  const matchValues = (a, b) => a.value === b.value;

  const handleSelection = (selected) => {
    selectOption && selectOption(selected);
    onChange && onChange([...values, selected]);
  };

  const handleNewOption = (newItem) => {
    const transItem = transformNewItem ? transformNewItem(newItem) : newItem;
    if ((validateNewItem && validateNewItem(transItem)) || !validateNewItem) {
      newOption && newOption(transItem);
      onChange && onChange([...values, { value: transItem, label: transItem }]);
    }
  };

  const handleRemoval = (selected, idx) => {
    removeOption && removeOption(selected);
    const newValues = [...values];
    if (idx >= 0) {
      newValues.splice(idx, 1);
    }
    onChange && onChange(newValues);
  };

  const chips = (values || []).map((option: Option<T>, idx: number) => {
    if (option.tooltip) {
      return (
        <Tooltip key={option.value?.toString() ?? '' + idx} content={option.tooltip ? option.tooltip : ''}>
          <Chip
            label={option.label}
            editable={!option.isDisabled && !disabled}
            onClick={() => handleRemoval(option, idx)}
          />
        </Tooltip>
      );
    } else {
      return (
        <Chip
          key={option.value?.toString() ?? '' + idx}
          label={option.label}
          editable={!option.isDisabled && !disabled}
          onClick={() => handleRemoval(option, idx)}
        />
      );
    }
  });

  const idToUse = id ? id : useId();

  let optionsToShow = options;

  if (!allowMultiSelect) {
    optionsToShow = options.filter((option) => !values.find((value) => matchValues(option, value)));
  }

  return (
    <div className={cx('form-element', 'container', { 'container-fullwidth': fullWidth, fw: fullWidth })}>
      <label
        id={`${idToUse}-label`}
        className={cx('label', { 'sr-only': !label && label !== '', 'label--disabled': disabled })}>
        {label}
      </label>
      <div className={cx('chips')}>{chips}</div>
      <Dropdown
        {...otherProps}
        options={optionsToShow}
        placeholder={placeholder}
        disabled={disabled}
        id={idToUse}
        fullWidth={fullWidth}
        onChange={handleSelection}
        newOption={handleNewOption}
        isCreateable={allowAddNew}
        value={undefined} // ensures the dropdown always shows the placeholder text
      />
    </div>
  );
};

MultiSelectDropdown.PortalTags = PortalTagMultiSelectDropdown;

export default MultiSelectDropdown;
